import { useState, type ReactNode } from "react";
import { useSubmit } from "../app/use-submit.js";
import { Failure } from "./Async.jsx";

/**
 * "Delete", wherever deleting belongs.
 *
 * One control for every kind of thing, because a person who has learned what
 * deleting a bill does should not have to find out separately what deleting a
 * photograph does. It is the same promise each time: out of the portal now,
 * back on request for thirty days, gone after that.
 *
 * IT ASKS BEFORE IT ACTS, and the asking is the point rather than a formality.
 * The confirmation says what will happen, carries any warning the caller
 * passes -- a photograph still inside its retention period, say -- and takes
 * an optional reason, which is stored with the item and shown in the bin. In
 * six months "why is this gone" is a question somebody will ask.
 */
export function DeleteAction({
  what,
  warning,
  onDelete,
  onDone,
  label = "Delete",
}: {
  /** Named in the confirmation: "Delete this photograph?" */
  readonly what: string;
  /** Anything the person should weigh before going ahead. */
  readonly warning?: ReactNode;
  readonly onDelete: (reason: string | undefined) => Promise<void>;
  readonly onDone?: () => void;
  readonly label?: string;
}): ReactNode {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const { submit, busy, error } = useSubmit();

  if (!asking) {
    return (
      <button type="button" className="secondary" onClick={() => setAsking(true)}>
        {label}
      </button>
    );
  }

  const confirm = (): void => {
    void submit(async () => {
      await onDelete(reason.trim() === "" ? undefined : reason.trim());
      setAsking(false);
      setReason("");
      onDone?.();
    });
  };

  return (
    <div className="confirm" data-testid="delete-confirm">
      <p className="confirm__title">Delete this {what}?</p>
      {warning ? <p className="confirm__warning">{warning}</p> : null}
      <p className="small muted">
        It leaves the portal now. You can put it back from the recycle bin for
        thirty days, after which it is destroyed and cannot be recovered.
      </p>
      <label className="field">
        <span className="field__label">Why (optional)</span>
        <input
          type="text"
          value={reason}
          maxLength={500}
          disabled={busy}
          placeholder="Sent to the wrong survey"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      {error ? <Failure what={`the ${what}`} error={error} /> : null}
      <div className="confirm__actions">
        <button type="button" onClick={confirm} disabled={busy}>
          {busy ? "Deleting…" : "Delete"}
        </button>
        <button
          type="button"
          className="secondary"
          disabled={busy}
          onClick={() => {
            setAsking(false);
            setReason("");
          }}
        >
          Keep it
        </button>
      </div>
    </div>
  );
}
